import { getChainOfLayer } from '@cronos-app/sdk';
import { currentWallet } from '@cronos-app/wallet';
import { useQuery } from '@tanstack/react-query';
import { fetchFromSubgraph } from '@ui/utils/fetchFromSubgraph';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { cdcCardTopUpHistorySubgraph } from '../configs/subgraph';
import { useCardLayer } from '../useCardContextStore';

export interface TopUpRecord {
  from: string;
  to: string;
  fromAmount: string;
  toAmount: string;
  timestamp: number;
  depositAddress: string;
}

export const useTopUpHistoryList = (
  selectedDepositAddress: string | undefined,
  firstPageSize = 5,
  pageSize = 10,
) => {
  const account = currentWallet.useAccount();
  const network = currentWallet.useDesiredNetwork();
  const layer = useCardLayer();
  const chainId = useMemo(
    () => getChainOfLayer(network, layer),
    [layer, network],
  );
  const [list, setList] = useState<TopUpRecord[]>([]);
  const loadingRef = useRef(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasNext, setHasNext] = useState(true);
  const nextSize = list.length ? pageSize : firstPageSize;
  const fetchMore = useCallback(async () => {
    if (loadingRef.current || !account) return;
    setIsLoading(true);
    loadingRef.current = true;
    const pageData = await fetchHistoryList(
      chainId,
      account,
      selectedDepositAddress,
      list.length,
      list.length + nextSize,
    );
    setList((l) => [...l, ...pageData]);
    setHasNext(pageData.length === nextSize);
    setIsLoading(false);
    loadingRef.current = false;
  }, [account, chainId, selectedDepositAddress, list.length, nextSize]);
  useEffect(() => {
    setList([]);
  }, [selectedDepositAddress]);
  useEffect(() => {
    !list.length && fetchMore();
  }, [fetchMore, list.length]);
  return {
    list,
    isLoading,
    hasNext,
    fetchMore,
  };
};

export const useTopUpHistoryDepositAddressesKey =
  'useTopUpHistoryDepositAddressesKey';
export const useTopUpHistoryDepositAddresses = () => {
  const account = currentWallet.useAccount();
  const network = currentWallet.useDesiredNetwork();
  const layer = useCardLayer();
  const chainId = useMemo(
    () => getChainOfLayer(network, layer),
    [layer, network],
  );

  return useQuery({
    queryKey: [useTopUpHistoryDepositAddressesKey, account, chainId],
    queryFn: () => {
      if (!account) throw new Error('No account');
      return fetchTopUpDepositAddresses(chainId, account);
    },
    enabled: !!account,
  });
};

const fetchHistoryList = async (
  chainId: number,
  account: string,
  selectedDepositAddress: string | undefined,
  pageStart: number,
  pageSize: number,
) => {
  const subgraphUrl = cdcCardTopUpHistorySubgraph[chainId];
  if (!subgraphUrl) throw new Error('No subgraph');
  const { data } = await fetchFromSubgraph<{
    deposits: {
      address: string;
      token: string;
      amount: string;
      timestamp: string;
      txnHash: string;
      depositAddress: string;
      swap?: {
        inToken: string;
        inTokenAmount: string;
        outToken: string;
        outTokenAmount: string;
      };
    }[];
  }>({
    subgraphUrl,
    query: `
      query TopUpHistory (
        $address: Bytes!
        $selectedDepositAddress: Bytes
        $pageStart: Int!
        $pageSize: Int!
      ) {
        deposits (where: { address: $address, ${
          selectedDepositAddress
            ? 'depositAddress: $selectedDepositAddress'
            : ''
        } }, skip: $pageStart, first: $pageSize, orderBy: timestamp, orderDirection: desc) {
          id
          token
          txnHash
          amount
          address
          depositAddress
          timestamp
          swap {
            id
            inToken
            inTokenAmount
            outToken
            outTokenAmount
            txnHash
          }
        }
      }
    `,
    variables: {
      address: account.toLowerCase(),
      pageStart,
      pageSize,
      selectedDepositAddress: selectedDepositAddress?.toLowerCase(),
    },
  });
  return data.deposits.map((e) => ({
    from: e.swap?.inToken ?? e.token,
    to: e.swap?.outToken ?? e.token,
    fromAmount: e.swap?.inTokenAmount ?? e.amount,
    toAmount: e.swap?.outTokenAmount ?? e.amount,
    timestamp: Number(e.timestamp) * 1000,
    depositAddress: e.depositAddress,
  }));
};

const fetchTopUpDepositAddresses = async (chainId: number, account: string) => {
  const subgraphUrl = cdcCardTopUpHistorySubgraph[chainId];
  if (!subgraphUrl) throw new Error('No subgraph');
  const {
    data: { depositAddress },
  } = await fetchFromSubgraph<{
    depositAddress: {
      depositAddress: string;
      history: string[];
    } | null;
  }>({
    subgraphUrl,
    query: `
      query TopUpDepositAddresses(
        $address: ID!
      ) {
        depositAddress (id: $address) {
          id
          address
          depositAddress
          history
        }
      }
    `,
    variables: {
      address: account.toLowerCase(),
    },
  });
  const ans: string[] = [];
  if (!depositAddress) return ans;
  const { depositAddress: cur, history } = depositAddress;
  const visited = new Set<string>();
  ans.push(cur);
  visited.add(cur);
  for (let i = history.length - 1; i >= 0; i -= 1) {
    // from the latest to the oldest, and remove duplicates
    const addr = history[i];
    if (visited.has(addr)) continue;
    ans.push(addr);
    visited.add(addr);
  }
  return ans;
};
